'use client';

import { useState } from 'react';
import { Check, Minus, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslations } from 'next-intl';

// Controls shared by every config-file editor (server.properties, Paper/Spigot/Bukkit/Leaf/Gale
// YAML, velocity.toml) so settings look and behave the same everywhere.

export function BooleanToggle({ checked, onChange, disabled }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={cn(
        'relative h-8 w-[60px] shrink-0 overflow-hidden rounded-md border border-border bg-muted p-0.5 transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
      )}
    >
      <span
        className={cn(
          'flex h-full w-[28px] items-center justify-center rounded-[5px] text-white shadow transition-transform duration-150',
          checked ? 'translate-x-[28px] bg-emerald-500' : 'translate-x-0 bg-red-500',
        )}
      >
        {checked ? <Check className="h-4 w-4" strokeWidth={3} /> : <X className="h-4 w-4" strokeWidth={3} />}
      </span>
    </button>
  );
}

function decimalsOf(value: string) {
  const dot = value.indexOf('.');
  return dot === -1 ? 0 : value.length - dot - 1;
}

/** Number input with +/- buttons. Keeps decimals when the value has them (step = smallest digit). */
export function NumberStepper({
  value, onChange, disabled, min, max, label, allowDecimals,
}: {
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  label?: string;
  allowDecimals?: boolean;
}) {
  const t = useTranslations('Auto.HostingConfigControls');
  const lo = min ?? Number.NEGATIVE_INFINITY;
  const hi = max ?? Number.POSITIVE_INFINITY;
  const decimals = allowDecimals ? decimalsOf(value) : 0;
  const step = (direction: number) => {
    const current = Number.parseFloat(value);
    const base = Number.isFinite(current) ? current : 0;
    const delta = direction * (decimals > 0 ? Math.pow(10, -Math.min(decimals, 6)) : 1);
    const next = Math.min(hi, Math.max(lo, base + delta));
    onChange(decimals > 0 ? next.toFixed(decimals) : String(Math.round(next)));
  };
  return (
    <div className="flex h-8 w-36 shrink-0 items-stretch overflow-hidden rounded-md border border-border bg-background">
      <input
        inputMode={allowDecimals ? 'decimal' : 'numeric'}
        disabled={disabled}
        value={value}
        onChange={(e) => onChange(e.target.value.replace(allowDecimals ? /[^0-9.\-]/g : /[^0-9-]/g, ''))}
        className="min-w-0 flex-1 bg-transparent px-2 text-right text-sm font-semibold tabular-nums outline-none disabled:opacity-50"
        aria-label={label}
      />
      <div className="flex w-6 flex-col border-l border-border">
        <button type="button" disabled={disabled} onClick={() => step(1)} className="flex flex-1 items-center justify-center hover:bg-muted disabled:opacity-50" aria-label={t('increase')}>
          <Plus className="h-3 w-3" />
        </button>
        <button type="button" disabled={disabled} onClick={() => step(-1)} className="flex flex-1 items-center justify-center border-t border-border hover:bg-muted disabled:opacity-50" aria-label={t('decrease')}>
          <Minus className="h-3 w-3" />
        </button>
      </div>
    </div>
  );
}

/** Editable list of short values shown as chips. */
export function ListEditor({ items, onChange, disabled, placeholder = 'Add an entry…' }: {
  items: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  placeholder?: string;
}) {
  const t = useTranslations('Auto.HostingConfigControls');
  const [draft, setDraft] = useState('');
  const add = () => {
    const v = draft.trim();
    if (!v) return;
    onChange([...items, v]);
    setDraft('');
  };
  return (
    <div className="flex w-full flex-col gap-2">
      {items.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {items.map((item, i) => (
            <span key={`${item}-${i}`} className="inline-flex max-w-full items-center gap-1 rounded-md border border-border bg-muted px-2 py-0.5 font-mono text-xs">
              <span className="truncate">{item}</span>
              {!disabled && (
                <button type="button" onClick={() => onChange(items.filter((_, idx) => idx !== i))} className="text-muted-foreground hover:text-destructive" aria-label={`Remove ${item}`}>
                  <X className="h-3 w-3" />
                </button>
              )}
            </span>
          ))}
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{t('empty')}</p>
      )}
      {!disabled && (
        <div className="flex gap-1.5">
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            placeholder={placeholder}
            className="h-8 min-w-0 flex-1 rounded-md border border-input bg-background px-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
          <button type="button" onClick={add} className="inline-flex h-8 items-center gap-1 rounded-md border border-border px-2 text-xs hover:bg-muted">
            <Plus className="h-3 w-3" /> Add
          </button>
        </div>
      )}
    </div>
  );
}
